import React, { useState, useEffect } from 'react';
import { reviewAPI } from '../services/api';
import './CompanyReviewsManager.css';
import Icon from './Icon';

function CompanyReviewsManager({ user }) {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'unanswered', 'answered'
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [filter]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await reviewAPI.getCompanyReviews({ status: filter });
      setReviews(response.data.reviews || []);
      setStats(response.data.stats || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (reviewId) => {
    if (!responseText.trim()) {
      setError('Response cannot be empty');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await reviewAPI.respondToReview(reviewId, responseText);
      setSuccess('Response added successfully!');
      setRespondingTo(null);
      setResponseText('');
      fetchReviews();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add response');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (loading) {
    return <div className="loading">Loading reviews...</div>;
  }

  return (
    <div className="company-reviews-manager">
      {/* Stats Summary */}
      {stats && (
        <div className="reviews-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.average_rating.toFixed(1)}</div>
            <div className="stat-label">Average Rating</div>
            <div className="stat-stars">{renderStars(Math.round(stats.average_rating))}</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Total Reviews</div>
          </div>
          <div className="stat-card highlight">
            <div className="stat-value">{stats.unanswered}</div>
            <div className="stat-label">Unanswered</div>
          </div>
        </div>
      )}

      {/* Rating Distribution */}
      {stats && stats.rating_distribution && (
        <div className="rating-distribution">
          <h3>Rating Distribution</h3>
          {[5, 4, 3, 2, 1].map(rating => {
            const count = stats.rating_distribution[rating] || 0;
            const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
            return (
              <div key={rating} className="rating-bar">
                <span className="rating-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {rating} <Icon name="star" color="warning" darkMode={true} size={14} />
                </span>
                <div className="bar-container">
                  <div className="bar-fill" style={{ width: `${percentage}%` }}></div>
                </div>
                <span className="rating-count">{count}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="review-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Reviews ({stats?.total || 0})
        </button>
        <button
          className={`filter-btn ${filter === 'unanswered' ? 'active' : ''}`}
          onClick={() => setFilter('unanswered')}
        >
          Unanswered ({stats?.unanswered || 0})
        </button>
        <button
          className={`filter-btn ${filter === 'answered' ? 'active' : ''}`}
          onClick={() => setFilter('answered')}
        >
          Answered
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Reviews List */}
      <div className="reviews-list">
        {reviews.length === 0 ? (
          <div className="no-reviews">
            <div className="no-reviews-icon" style={{ display: 'flex', justifyContent: 'center' }}>
              <Icon name="chat" color="muted" darkMode={true} size={48} />
            </div>
            <p>No reviews yet</p>
          </div>
        ) : (
          reviews.map(review => (
            <div key={review.id} className="review-card">
              <div className="review-header">
                <div className="review-user">
                  <div className="user-avatar">{review.user_name?.charAt(0) || '?'}</div>
                  <div>
                    <div className="user-name">{review.user_name || 'Anonymous'}</div>
                    <div className="review-date">
                      {new Date(review.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
                <div className="review-rating">{renderStars(review.rating)}</div>
              </div>

              {review.comment && (
                <div className="review-comment">{review.comment}</div>
              )}

              {review.service && (
                <div className="review-service-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="service" color="secondary" darkMode={true} size={16} />
                  {review.service.description || 'Service'}
                  {review.service.service_date && (
                    <span className="service-date">
                      {' • '}
                      {new Date(review.service.service_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}

              {/* Company Response */}
              {review.company_response ? (
                <div className="company-response">
                  <div className="response-header" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon name="business" color="primary" darkMode={true} size={16} />
                    <strong>Response from {review.response_author_name || 'Company'}</strong>
                    <span className="response-date">
                      {new Date(review.responded_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="response-text">{review.company_response}</div>
                </div>
              ) : (
                <div className="response-actions">
                  {respondingTo === review.id ? (
                    <div className="response-form">
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        placeholder="Write your response... (like Google Reviews)"
                        rows="4"
                        className="response-textarea"
                      />
                      <div className="response-buttons">
                        <button
                          onClick={() => handleRespond(review.id)}
                          disabled={submitting}
                          className="btn btn-primary"
                        >
                          {submitting ? 'Sending...' : 'Send Response'}
                        </button>
                        <button
                          onClick={() => {
                            setRespondingTo(null);
                            setResponseText('');
                          }}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setRespondingTo(review.id)}
                      className="btn btn-secondary btn-respond"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Icon name="chat" color="primary" darkMode={true} size={16} />
                      Respond to Review
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CompanyReviewsManager;

